import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {Mental} from "../../Mental";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TimelineMax} from "gsap";
import {MentalHelper} from "../../utils/MentalHelper";
import {IBetWayWinData, IMentalGameData} from "../../server/MentalData";
import {FeaturePresentationHelper} from "../../utils/FeaturePresentationHelper";

export class BaseGameIdlePresentation implements FlowStatePresentation {
    private _tl: TimelineMax = new TimelineMax();
    private _toggleTl: TimelineMax = new TimelineMax();
    private _winFrameCount: number = 0;

    private static resetWinBoxPresentData(): boolean[][][] {
        const numberOfSymbolPerReel: number = 3;
        const totalNumOfReels: number = 5;
        const winFrame: boolean[][][] = [];
        for (let i: number = 0; i < totalNumOfReels; i++) {
            winFrame[i] = [];
            for (let j: number = 0; j < numberOfSymbolPerReel; j++) {
                winFrame[i][j] = [];
                for (let k: number = 0; k < Mental.model.gameData.currentSymbolSize[i][j]; k++) {
                    winFrame[i][j].push(false);
                }
            }
        }
        return winFrame;
    }

    public start(flowStateController: FlowStateController): void {
        if (Mental.model.data.mode.indexOf("FREESPIN") > -1 && Mental.model.data.nextMode === GameMode.NORMAL) {
            Mental.keypadPlugin.setZeroBetSpinCounter(-1);
            for (let i = 0; i < Mental.model.data.stickyFires.length; i++) { // to reset xways count after  fs
                Mental.xWaysCounter._stickyFFCounterForFS[i] = 0;
            }
        }

        if (Mental.model.data.nextMode === GameMode.NORMAL) {
            Mental.background.onChangeMode(Mental.model.data.nextMode);
            Mental.scatterAnticipation.onChangeMode(Mental.model.data.nextMode);
            Mental.reelArea.onChangeMode(Mental.model.data.nextMode);
        }

        this._tl = MentalHelper.timeLineKiller(this._tl, new TimelineMax());
        this._tl.add(() => Mental.background.flickBackgroundLight(), Mental.background.getRandomTime()); // #131
        flowStateController.setSkipCallback(() => {
            flowStateController.finish();

            this.stop();
        });

        const gameData: IMentalGameData = Mental.model.gameData;
        if (gameData && gameData.betWayWins && gameData.betWayWins.length > 0) {
            this._tl.add(() => this.getWinLineToggleAnimation(), 0.15);
        }
    }

    // #423
    public stop(): void {
        if (this._tl) {
            Mental.background.clearFlickAnimation();
            this._tl.clear();
            this._tl.kill();

            if (this._toggleTl) {
                this._toggleTl.clear();
                this._toggleTl.kill();
            }

            Mental.winWays.removeToggleWinWaysItems();
        }
    }

    private animateSymbolGroup(winData: IBetWayWinData, animName: string, alreadyWinBoxPresent: boolean[][][] = []): void {
        for (let i: number = 0; i < winData.reelWinPositions.length; i++) {  // reels
            for (let j: number = 0; j < winData.reelWinPositions[i].length; j++) { //reel
                const symbol: boolean[] = winData.reelWinPositions[i][j];
                let resizedReel: boolean = false;

                if (i === 1 || i === 3) {
                    const s2Index: number = (i === 1) ? 0 : 1;
                    const enhancerIndex: number = (i === 1) ? 2 : 3;
                    resizedReel = (Mental.model.gameData.xWaysPlayed[s2Index] || Mental.model.gameData.xWaysPlayed[enhancerIndex]);
                }

                for (let k: number = 0; k < symbol.length; k++) { // symbol
                    if (symbol[k] && (!alreadyWinBoxPresent.length || (alreadyWinBoxPresent.length && !alreadyWinBoxPresent[i][j][k]))) {

                        if (alreadyWinBoxPresent.length) {
                            alreadyWinBoxPresent[i][j][k] = true;
                        }
                        if ((i === 1 || i === 3) && j === 2) { //  there could be xWays on reel, but not on enhancer.
                            const enhancerIndex: number = (i === 1) ? 2 : 3;
                            resizedReel = (Mental.model.gameData.xWaysPlayed[enhancerIndex]);
                        }

                        const heightPerVisibleSymAfterResize: number = resizedReel ? FeaturePresentationHelper._symbolHeightOnReel[i] : 198 / symbol.length;
                        let isEnhancePos = ((i === 1 || i === 3) && (j === 2));
                        let isSingleSymInEnhancer: boolean = (isEnhancePos && symbol.length == 1);

                        Mental.winHighlight.playToggleAnimation({
                            reelId: i,
                            symbolId: j,
                            innerSymbolId: k,
                            animName: animName,
                            resizedReel: resizedReel,
                            singleSymbolInEnhancer: isSingleSymInEnhancer,
                            symbolHeight: heightPerVisibleSymAfterResize,
                            winFrameIndex: this._winFrameCount++,
                            timeScale: (1 / 2) // #145
                        });
                    }
                }
            }
        }
    }

    private getWinLineToggleAnimation(): void {
        const winGroups: IBetWayWinData[] = Mental.model.gameData.betWayWins,
            animName: string = (Mental.model.gameData.currentWinRatioConfig.duration > 0.001 ? "intenseFrame" : "frame"),
            duration: number = 0.8 * 2; // #145

        this._toggleTl = MentalHelper.timeLineKiller(this._toggleTl, new TimelineMax({
            repeat: -1,
            repeatDelay: duration - 0.4 // With the value of "repeatDelay", we can control the pause present after all symbols are shown together.
        }));

        // show 1 symbol group at a time(gap between 2 groups is 800ms)
        for (let groups: number = 0; groups < winGroups.length; groups++) {
            this._toggleTl.add(() => {
                const winGroup: IBetWayWinData = winGroups[groups];
                this._winFrameCount = 0;
                this.animateSymbolGroup(winGroup, animName);

                // #227, #420
                if (!MentalHelper.isSingleWinBelowStake()) {
                    Mental.winWays.showToggleWinWaysItems((winGroups.length === 1 ? Mental.model.gameData.totalBetWayWinnings : winGroup.multipliedAmount), winGroup.numberOfCombinations);
                }
            }, (groups * duration));

            // #207
            this._toggleTl.add(() => Mental.winWays.fadeOutToggleWinWaysItems(), ((groups * duration) + (duration / 2)));
        }

        // show all symbols together
        if (winGroups.length > 1) {
            this._toggleTl.add(() => {
                let totalWaysToggle: number = 0;
                const alreadyWinBoxPresent: boolean[][][] = BaseGameIdlePresentation.resetWinBoxPresentData();
                this._winFrameCount = 0;
                for (let groups: number = 0; groups < winGroups.length; groups++) {
                    const winGroup: IBetWayWinData = winGroups[groups];
                    this.animateSymbolGroup(winGroup, animName, alreadyWinBoxPresent);

                    totalWaysToggle += winGroup.numberOfCombinations;
                }

                // #227, #420
                if (!MentalHelper.isSingleWinBelowStake()) {
                    Mental.winWays.showToggleWinWaysItems(Mental.model.gameData.totalBetWayWinnings, totalWaysToggle);
                }
            }, (winGroups.length * duration));

            // #207
            this._toggleTl.add(() => Mental.winWays.fadeOutToggleWinWaysItems(), ((winGroups.length * duration) + (duration / 2)));
        } else {
            this._toggleTl.add(() => Mental.winWays.fadeOutToggleWinWaysItems(), duration / 2);
        }
    }
}